import mixpanel from 'mixpanel-browser';
import type PptxGenJS from 'pptxgenjs';
import type { RefObject } from 'react';

import { type ChartType, FeatureTrackingEvents } from '@revelio/core';
import type { UploadFileResult } from '@revelio/data-access';
import { getPrimaryColor, ReplotChartNames } from '@revelio/replots';
import type { ToastApi } from '@revelio/ui';

import {
  DataType,
  type TransitionBreakdownData,
} from '../../../../DataProvider/types';
import type { MetricValues } from '../../../../PageConfigProvider/types/metric';
import { MIXED_STATS_DATA_TYPE } from '../registry';
import {
  captureChartImage,
  isFlowFigureChartType,
  isImageOnlyChartType,
} from './ppt-chart-image';
import {
  buildSheetData,
  generateChartData,
  processMixedStatsData,
} from './ppt-data-processor';
import {
  addBoxPlotSlide,
  addChartImageSlide,
  addDivergingBarComboSlide,
  addFooterSlide,
  addGaugeSlide,
  addMixedStatsSlide,
  addSlideContent,
  addTableSlide,
  addTextSlide,
  addTitleSlideDetails,
  type BoxPlotEntityColor,
  buildBoxPlotChartSpec,
  chartTitleWithSelection,
  type FlowFigures,
  hasExportableGaugeContent,
  isBoxPlotDataSet,
} from './ppt-slide-generator';
import {
  buildStackedDualLabels,
  type DualLabelStyle,
  injectStackedDualLabels,
  type StackedDualLabelEntry,
} from './ppt-stacked-dual-labels';
import { defineSlideMasters, PptxChartType, pptTheme } from './ppt-theme';

export type PptReplotChartType = ReplotChartNames;

type ChartExportParams = {
  title: string;
  exportTitle?: string;
  chartType?: PptReplotChartType;
  plotChartType?: ChartType;
  selectedSegments?: { name: string }[];
  data?: unknown;
  dataType?: string;
  textContent?: string;
  gaugeMainText?: string;
  gaugeValue?: number;
  gaugeColors?: string[];
  gaugeMinLabel?: string;
  gaugeMaxLabel?: string;
  gaugeEntities?: { label?: string; value: number }[];
  /** Rendered chart node, used to capture chart types PowerPoint can't draw. */
  captureRef?: RefObject<HTMLDivElement>;
  /** Plot metric — decides flow direction for transitions scale figures. */
  metric?: MetricValues;
  selectionLabel?: string;
  boxEntityColors?: BoxPlotEntityColor[];
  yAxisBounds?: { min: number; max: number };
};

export type PptCoverInfo = {
  title?: string;
  parameters?: { label: string; value: string }[];
};

type SingleChartExportParams = ChartExportParams & {
  cover?: PptCoverInfo;
  /** The download name from getFileName; the .pptx is named after it. */
  downloadName?: string;
};

type PptExportResult = {
  getFile: () => Promise<void>;
  getBlob: () => Promise<{ blob: Blob; fileName: string } | null>;
};

const toSafeFileName = (name: string): string =>
  name
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

export const deckFileName = (
  baseName: string,
  cover?: PptCoverInfo,
  downloadName?: string
): string => {
  const stem =
    toSafeFileName(downloadName ?? '') ||
    toSafeFileName(cover?.title ?? '') ||
    toSafeFileName(baseName) ||
    'dashboard-export';
  return `${stem}.pptx`;
};

/** pptxgenjs is large and only needed once an export runs, so load it then. */
export const createPptx = async (): Promise<PptxGenJS> => {
  const { default: Presentation } = await import('pptxgenjs');
  return new Presentation();
};

export const mapReplotToPptChart = (
  replotChartType?: PptReplotChartType | null
) => {
  switch (replotChartType) {
    case ReplotChartNames.LineChart:
      return {
        type: PptxChartType.line,
        opts: {
          lineGrouping: 'percentStacked',
          lineDataSymbol: 'none',
          lineSize: 1,
          showLegend: true,
          legendPos: 'r',
          catLabelFormatCode: 'yyyy-mm',
          catAxisLabelFrequency: '10',
        },
      };
    case ReplotChartNames.BarChart:
      return {
        type: PptxChartType.bar,
        opts: {
          barDir: 'bar',
          // On screen every bar is painted one colour (`getPrimaryColor()` in
          // bar-chart.tsx), so pass a single colour rather than the categorical
          // palette — otherwise pptxgenjs varies it per bar.
          chartColors: [getPrimaryColor().replace('#', '')],
          // The dashboard shows the value at each bar's end; a deck has no
          // tooltips, so bake it in (format derived from the data downstream).
          showValue: true,
          dataLabelPosition: 'outEnd',
        },
      };
    case ReplotChartNames.StackedBarChartHorizontal:
      return {
        type: PptxChartType.bar,
        opts: {
          barGrouping: 'percentStacked',
          barDir: 'bar',
          showLegend: true,
          legendPos: 'b',
          // Composition charts render with the breakdown ramp on screen.
          chartColors: pptTheme.colors.breakdownPalette,
          // Bake each segment's share in, replacing the on-screen hover
          // tooltip. Stacked bars only allow inside label positions — 'outEnd'
          // corrupts the file.
          showValue: true,
          dataLabelPosition: 'ctr',
        },
      };
    case ReplotChartNames.GroupedBarChartHorizontal:
      return {
        type: PptxChartType.bar,
        opts: {
          barDir: 'bar',
          showLegend: true,
          legendPos: 'b',
          barGapWidthPct: 25,
          // Replaces the on-screen hover tooltip (format derived downstream).
          showValue: true,
          dataLabelPosition: 'outEnd',
        },
      };
    case ReplotChartNames.DivergingBarChart:
      return {
        type: PptxChartType.bar,
        opts: {
          barDir: 'bar',
          barGrouping: 'stacked',
          showLegend: false,
          axisLabelFormatCode: '#%',
          catAxisLabelPos: 'low',
          valueBarColors: true,
          chartColors: [
            pptTheme.colors.divergingPositive.replace('#', ''),
            pptTheme.colors.divergingNegative.replace('#', ''),
          ],
          catAxisLabelFontSize: 10,
          // Segment shares baked in; the negative side is only a drawing
          // convention, so the pos;neg format pattern keeps labels unsigned.
          // The empty zero section hides the 0% of the side a bar does not use.
          showValue: true,
          dataLabelPosition: 'ctr',
          dataLabelFormatCode: '0%;0%;',
        },
      };
    case ReplotChartNames.BoxPlot:
      return {
        type: PptxChartType.bar,
        opts: {
          barGrouping: 'clustered',
          barDir: 'bar',
          showLegend: true,
          legendPos: 'b',
          catAxisMultiLevelLabels: true,
          catAxisLabelFontFace: 'Arial',
          catAxisLabelFontSize: 10,
        },
      };
    case ReplotChartNames.KDE:
      return {
        type: PptxChartType.area,
        opts: {
          showLegend: true,
          legendPos: 'b',
          chartColorsOpacity: 60,
        },
      };
    case ReplotChartNames.VolumeChart:
      return {
        type: 'combo',
        opts: {
          showLegend: true,
          legendPos: 'b',
        },
      };
    case ReplotChartNames.DivergingBarCombo:
      return {
        type: 'combo-diverging',
        opts: {},
      };
    case ReplotChartNames.TableChart:
      // Tables are rendered via addTableSlide before this mapping is reached;
      // return a no-op placeholder so the type stays explicit here.
      return null;
    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case ReplotChartNames.Sankey:
    default:
      return {
        type: PptxChartType.bar,
        opts: {
          showLegend: true,
          legendPos: 'b',
        },
      };
  }
};

/** Label styling matches the charts' own data labels (brand font, ink text). */
const dualLabelStyle = (): DualLabelStyle => ({
  colorHex: pptTheme.colors.text.replace('#', ''),
  fontFace: pptTheme.fonts.face,
  fontSize: pptTheme.fonts.dataLabelSize,
});

/**
 * Produce the final deck blob, rewriting stacked-chart labels to the
 * "count / share" form when any were collected.
 */
const finalizePptxBlob = async (
  pptx: PptxGenJS,
  dualLabels: StackedDualLabelEntry[]
): Promise<Blob> => {
  const blob = (await pptx.write({ outputType: 'blob' })) as Blob;
  return dualLabels.length > 0
    ? injectStackedDualLabels(blob, dualLabels, dualLabelStyle())
    : blob;
};

/** Browser download for a post-processed blob (pptx.writeFile can't be used —
 * the deck was rewritten after pptxgenjs finished). */
const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Give the browser time to start the download before the URL is revoked.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
};

const addDeckTitleSlide = (
  pptx: PptxGenJS,
  baseName: string,
  cover?: PptCoverInfo
) => {
  const titleSlide = pptx.addSlide({ masterName: 'TITLE_SLIDE' });
  titleSlide.addText(cover?.title?.trim() || baseName, {
    placeholder: 'report_name',
  });
  if (cover?.parameters?.length) {
    addTitleSlideDetails(titleSlide, cover.parameters);
  }
};

const createSinglePptxDoc = async (
  params: SingleChartExportParams
): Promise<{
  pptx: PptxGenJS;
  baseName: string;
  fileName: string;
  slideAdded: boolean;
  dualLabels: StackedDualLabelEntry[];
}> => {
  const { title, exportTitle, cover } = params;

  const pptx = await createPptx();
  defineSlideMasters(pptx);

  const baseName = exportTitle || title;
  const fileName = deckFileName(baseName, cover, params.downloadName);

  addDeckTitleSlide(pptx, baseName, cover);

  const dualLabels: StackedDualLabelEntry[] = [];
  const slideAdded = await addChartSlide(pptx, params, dualLabels);

  addFooterSlide(pptx);

  return { pptx, baseName, fileName, slideAdded, dualLabels };
};

export const exportSingleChartToPpt = (
  params: SingleChartExportParams,
  // Caller-supplied context Mixpanel can't derive from the export params —
  // the same exporter serves per-plot and page-level paths, so `scope`
  // disambiguates them on the shared events.
  tracking?: { pageName?: string; chartId?: string }
): PptExportResult => ({
  async getFile() {
    const { fileName, slideAdded, pptx, dualLabels } =
      await createSinglePptxDoc(params);

    mixpanel.track(FeatureTrackingEvents.PPT_DOWNLOAD, {
      plot_title: params.title,
      scope: 'plot',
      page: tracking?.pageName,
      chart_id: tracking?.chartId,
      // Some exports only carry the Replots chart name, not the CustomDash
      // plot type — fall back so chart_type stays populated either way.
      chart_type: params.plotChartType ?? params.chartType,
      data_type: params.dataType,
    });

    if (!slideAdded) return;
    if (dualLabels.length === 0) {
      await pptx.writeFile({ fileName });
      return;
    }
    downloadBlob(await finalizePptxBlob(pptx, dualLabels), fileName);
  },
  async getBlob() {
    const { fileName, slideAdded, pptx, dualLabels } =
      await createSinglePptxDoc(params);
    if (!slideAdded) return null;
    const blob = await finalizePptxBlob(pptx, dualLabels);

    mixpanel.track(FeatureTrackingEvents.PPT_PREVIEW, {
      plot_title: params.title,
      scope: 'plot',
      page: tracking?.pageName,
      chart_id: tracking?.chartId,
      // Some exports only carry the Replots chart name, not the CustomDash
      // plot type — fall back so chart_type stays populated either way.
      chart_type: params.plotChartType ?? params.chartType,
      data_type: params.dataType,
    });

    return { blob, fileName };
  },
});

const MAX_FLOW_FIGURE_ROWS = 8;

/**
 * Scale figures for a captured transitions chart: a picture of a Sankey shows
 * proportions but no numbers, so list the largest flows and the dataset total
 * beside it. Direction mirrors `Sankey.tsx` — three-tier data carries explicit
 * inflow/outflow breakdowns; single-direction data orients by the plot metric.
 */
const buildFlowFigures = (
  chartParams: ChartExportParams
): FlowFigures | undefined => {
  const dataSet = chartParams.data as
    | { type?: unknown; data?: TransitionBreakdownData[] }
    | undefined;
  // The transitions providers tag their sets BREAKDOWN_SINGLE (the dedicated
  // FLOW_BREAKDOWN_SINGLE member is never emitted), so accept both. Callers
  // gate on flow chart types, which keeps composition-shaped BREAKDOWN_SINGLE
  // data (e.g. treemaps) out of here.
  if (
    !dataSet ||
    (dataSet.type !== DataType.FLOW_BREAKDOWN_SINGLE &&
      dataSet.type !== DataType.BREAKDOWN_SINGLE) ||
    !Array.isArray(dataSet.data)
  ) {
    return undefined;
  }

  const rows: FlowFigures['rows'] = [];
  const isInflowMetric = chartParams.metric === 'transitions_inflow';

  for (const entry of dataSet.data) {
    const primary = entry.dim ?? '';
    if (!primary) continue;

    const collect = (
      points: TransitionBreakdownData['breakdown'] | undefined,
      inflow: boolean
    ) => {
      for (const point of points ?? []) {
        const value = point.value ?? 0;
        if (value <= 0 || !point.dim) continue;
        rows.push(
          inflow
            ? { from: point.dim, to: primary, value }
            : { from: primary, to: point.dim, value }
        );
      }
    };

    if (entry.inflowBreakdown || entry.outflowBreakdown) {
      collect(entry.inflowBreakdown, true);
      collect(entry.outflowBreakdown, false);
    } else {
      collect(entry.breakdown, isInflowMetric);
    }
  }

  if (rows.length === 0) return undefined;

  const total = rows.reduce((sum, row) => sum + row.value, 0);
  rows.sort((a, b) => b.value - a.value);
  return { rows: rows.slice(0, MAX_FLOW_FIGURE_ROWS), total };
};

const addChartSlide = async (
  pptx: PptxGenJS,
  chartParams: ChartExportParams,
  dualLabels?: StackedDualLabelEntry[]
): Promise<boolean> => {
  const { exportTitle, selectedSegments, data, dataType } = chartParams;
  const title = chartTitleWithSelection(
    chartParams.title,
    chartParams.selectionLabel
  );

  if (chartParams.dataType === 'text') {
    const textBody = chartParams.textContent?.trim();
    if (textBody) {
      await addTextSlide(pptx, title, textBody, exportTitle);
      return true;
    }
    return false;
  }

  if (chartParams.plotChartType === 'gauge-v2') {
    if (
      !hasExportableGaugeContent({
        mainText: chartParams.gaugeMainText,
        value: chartParams.gaugeValue,
      })
    ) {
      console.warn(`No data to export for slide: ${title}`);
      return false;
    }
    const dataSlide = pptx.addSlide();
    addGaugeSlide(dataSlide, title, exportTitle, {
      mainText: chartParams.gaugeMainText,
      value: chartParams.gaugeValue,
      entities: chartParams.gaugeEntities,
      colors: chartParams.gaugeColors,
      minLabel: chartParams.gaugeMinLabel,
      maxLabel: chartParams.gaugeMaxLabel,
    });
    return true;
  }

  // PowerPoint has no native form for Sankey / chord / treemap. Capture the
  // rendered chart instead of letting it degrade into a meaningless bar chart.
  const imageChartType = getReplotChartType(chartParams);
  if (isImageOnlyChartType(imageChartType)) {
    const image = await captureChartImage(chartParams.captureRef?.current);
    if (!image) {
      console.warn(`No rendered chart to capture for slide: ${title}`);
      return false;
    }
    addChartImageSlide(
      pptx.addSlide(),
      title,
      exportTitle,
      image,
      isFlowFigureChartType(imageChartType)
        ? buildFlowFigures(chartParams)
        : undefined
    );
    return true;
  }

  const isDivergingBarComboExport =
    chartParams.plotChartType === 'diverging-bar-combo' ||
    chartParams.chartType === ReplotChartNames.DivergingBarCombo;

  if (isDivergingBarComboExport) {
    if (!data) {
      console.warn(`No data to export for slide: ${title}`);
      return false;
    }
    const dataSlide = pptx.addSlide();
    addDivergingBarComboSlide(
      dataSlide,
      title,
      exportTitle,
      data,
      dataType,
      selectedSegments
    );
    return true;
  }

  const replotChartType = getReplotChartType(chartParams);

  if (replotChartType === ReplotChartNames.BoxPlot && isBoxPlotDataSet(data)) {
    const boxPlotSpec = buildBoxPlotChartSpec(
      data,
      chartParams.boxEntityColors
    );
    if (boxPlotSpec) {
      addBoxPlotSlide(pptx.addSlide(), title, exportTitle, boxPlotSpec);
      return true;
    }
  }

  const { sheetData } = buildSheetData(data, dataType, replotChartType);
  if (!sheetData || sheetData.length === 0) {
    console.warn(`No data to export for slide: ${title}`);
    return false;
  }

  if (
    replotChartType === ReplotChartNames.StackedBarChartHorizontal &&
    dualLabels
  ) {
    // Must mirror the chart-title construction in `addSlideContent` — the
    // injected labels are matched back to their chart by its rendered title.
    const segmentNames = selectedSegments?.map((s) => s.name).join(', ');
    const chartTitleForLabels = segmentNames
      ? `${title} - ${segmentNames}`
      : title;
    dualLabels.push(...buildStackedDualLabels(chartTitleForLabels, sheetData));
  }

  if (replotChartType === ReplotChartNames.TableChart) {
    addTableSlide(
      pptx,
      sheetData as Record<string, string | number>[],
      title,
      exportTitle
    );
    return true;
  }

  const pptChart = mapReplotToPptChart(replotChartType);

  if (dataType === MIXED_STATS_DATA_TYPE) {
    const charts = processMixedStatsData(sheetData) || [];
    addMixedStatsSlide(pptx, charts, title, exportTitle);
  } else {
    const dataSlide = pptx.addSlide();
    const chartData = generateChartData(sheetData, replotChartType);

    addSlideContent(
      dataSlide,
      title,
      exportTitle,
      chartData,
      pptChart as unknown as {
        type: PptxGenJS.CHART_NAME;
        opts?: Record<string, unknown>;
      },
      replotChartType ?? undefined,
      selectedSegments,
      chartParams.yAxisBounds
    );
  }
  return true;
};

const createMultiplePptxDoc = async (
  charts: ChartExportParams[],
  cover?: PptCoverInfo,
  downloadName?: string
): Promise<{
  pptx: PptxGenJS;
  baseTitle: string;
  fileName: string;
  dualLabels: StackedDualLabelEntry[];
}> => {
  const pptx = await createPptx();
  defineSlideMasters(pptx);

  const baseTitle =
    charts[charts.length - 1]?.exportTitle || 'dashboard-export';

  addDeckTitleSlide(pptx, baseTitle || '', cover);

  const dualLabels: StackedDualLabelEntry[] = [];
  for (const c of charts) {
    await addChartSlide(pptx, c, dualLabels);
  }

  addFooterSlide(pptx);

  const finalName = deckFileName(baseTitle, cover, downloadName);
  return { pptx, baseTitle, fileName: finalName, dualLabels };
};

export const exportMultipleChartsToPpt = ({
  charts,
  cover,
  downloadName,
}: {
  charts: ChartExportParams[];
  cover?: PptCoverInfo;
  /** The download name from getFileName; the .pptx is named after it. */
  downloadName?: string;
}): PptExportResult => ({
  async getFile() {
    const { fileName, pptx, dualLabels } = await createMultiplePptxDoc(
      charts,
      cover,
      downloadName
    );

    // scope disambiguates this from the per-plot fire — a page-level PPT
    // download also emits `page download started`, so query per-plot activity
    // with scope = plot.
    mixpanel.track(FeatureTrackingEvents.PPT_DOWNLOAD, {
      plot_title: charts[charts.length - 1]?.title,
      scope: 'page',
      chart_count: charts.length,
    });

    if (dualLabels.length === 0) {
      await pptx.writeFile({ fileName });
      return;
    }
    downloadBlob(await finalizePptxBlob(pptx, dualLabels), fileName);
  },
  async getBlob() {
    if (!charts.length) return null;
    const { fileName, pptx, dualLabels } = await createMultiplePptxDoc(
      charts,
      cover,
      downloadName
    );
    const blob = await finalizePptxBlob(pptx, dualLabels);

    mixpanel.track(FeatureTrackingEvents.PPT_PREVIEW, {
      plot_title: charts[charts.length - 1]?.title,
      scope: 'page',
      chart_count: charts.length,
    });

    return { blob, fileName };
  },
});

const mapPlotChartTypeToReplotChartType = (
  plotChartType: ChartType
): PptReplotChartType | null => {
  switch (plotChartType) {
    case 'bar':
      return ReplotChartNames.BarChart;
    case 'line':
      return ReplotChartNames.LineChart;
    case 'stacked-bar':
      return ReplotChartNames.StackedBarChartHorizontal;
    case 'grouped-bar':
      return ReplotChartNames.GroupedBarChartHorizontal;
    case 'diverging-bar':
      return ReplotChartNames.DivergingBarChart;
    case 'diverging-bar-combo':
      return ReplotChartNames.DivergingBarCombo;
    case 'distribution':
      return ReplotChartNames.KDE;
    case 'volume':
      return ReplotChartNames.VolumeChart;
    case 'box':
      return ReplotChartNames.BoxPlot;
    case 'table':
      return ReplotChartNames.TableChart;
    case 'icicle':
      return ReplotChartNames.IcicleChart;
    default:
      return null;
  }
};

export const getReplotChartType = (
  chartParams: ChartExportParams
): PptReplotChartType | null => {
  const { chartType, plotChartType, dataType } = chartParams;
  if (chartType !== undefined) {
    return chartType;
  }

  if (plotChartType) {
    const mapped = mapPlotChartTypeToReplotChartType(plotChartType);
    if (mapped) return mapped;
  }

  if (dataType === DataType.DISTRIBUTION) {
    return ReplotChartNames.KDE;
  }

  console.warn('Unhandled data type for PPT export: ', dataType);

  return null;
};

export const showExportToast = (toast: ToastApi, promise: Promise<void>) =>
  toast.promise(promise, {
    loading: {
      title: 'Exporting file...',
      position: 'top-right',
    },
    success: {
      title: 'File exported',
      position: 'top-right',
      duration: 1500,
    },
    error: (error) => ({
      title: 'File export failed',
      description:
        (error instanceof Error && error.message) || 'Something went wrong.',
      position: 'top-right',
    }),
  });

export const showUploadToast = (
  toast: ToastApi,
  promise: Promise<UploadFileResult | undefined>
) =>
  toast.promise(promise, {
    loading: {
      title: 'Preparing your slides...',
      position: 'top-right',
    },
    success: {
      title: 'Slides are ready',
      position: 'top-right',
      duration: 1500,
    },
    error: (error) => ({
      title: 'Slides generation failed',
      description:
        (error instanceof Error && error.message) || 'Something went wrong.',
      position: 'top-right',
    }),
  });
